import './styles.scss'

import { TextStyleKit } from '@tiptap/extension-text-style'
import type { Editor } from '@tiptap/react'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions'

import React from 'react'
import { BoldIcon, CloseIcon, Heading1Icon, ItalicIcon, ListIcon, MinusIcon, OrderedListIcon, PilcrowIcon, StrikethroughIcon, TextQuoteIcon, UnderlineIcon, SaveIcon } from '../Icons/TooltipIcon'
import PasteCleanupExtension from './PasteCleanupExtension'

const extensions = [TextStyleKit, StarterKit]

interface MenuBarProps {
  editor: Editor
  onClose: () => void
  onSave?: (content: string) => void
}

function MenuBar({ editor, onClose, onSave }: MenuBarProps) {
  const [showShadow, setShowShadow] = React.useState(false);

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Attach this ref to the root div: ref={containerRef}
  React.useEffect(() => {
    const getContainer = () => containerRef.current ?? document.querySelector('.control-group');
    const checkHighlighted = () => {
      const container = getContainer() as HTMLElement | null;
      setShowShadow(Boolean(container?.closest('[data-wizard-highlighted]')));
    };

    checkHighlighted();

    const observerTarget = containerRef.current ?? document.body;
    const observer = new MutationObserver(() => checkHighlighted());
    observer.observe(observerTarget, { attributes: true, childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const editorState = useEditorState({
    editor,
    selector: ctx => {
      return {
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
      }
    },
  })

  return (
    <div className={"control-group absolute top-0 -translate-y-[175%] bg-table-body p-2 rounded-md z-10 w-full" + (showShadow ? " shadow-[rgba(0,0,15,0.3)_0px_-20px_5px_0px]" : "")}>{ /* */ }
      <div onClick={onClose}><CloseIcon className="absolute top-1 right-1 cursor-pointer h-4 w-4 hover:text-red-500" /></div>
      <div className="button-group">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editorState.canBold}
        className={editorState.isBold ? 'is-active' : ''}
      >
        <BoldIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editorState.canItalic}
        className={editorState.isItalic ? 'is-active' : ''}
      >
        <ItalicIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={!editorState.canUnderline}
        className={editorState.isUnderline ? 'is-active' : ''}
      >
        <UnderlineIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editorState.canStrike}
        className={editorState.isStrike ? 'is-active' : ''}
      >
        <StrikethroughIcon />
      </button>
      
      {/* 
      <button onClick={() => editor.chain().focus().unsetAllMarks().run()}>Clear marks</button>
      <button onClick={() => editor.chain().focus().clearNodes().run()}>Clear nodes</button>*/}
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editorState.isHeading1 ? 'is-active' : ''}
      >
        <Heading1Icon />
      </button>
      <button
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={editorState.isParagraph ? 'is-active' : ''}
      >
        <PilcrowIcon />
      </button>
      
      {/* <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editorState.isHeading2 ? 'is-active' : ''}
      >
        H2
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editorState.isHeading3 ? 'is-active' : ''}
      >
        H3
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        className={editorState.isHeading4 ? 'is-active' : ''}
      >
        H4
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
        className={editorState.isHeading5 ? 'is-active' : ''}
      >
        H5
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
        className={editorState.isHeading6 ? 'is-active' : ''}
      >
        H6
      </button> */}
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editorState.isBulletList ? 'is-active' : ''}
      >
        <ListIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editorState.isOrderedList ? 'is-active' : ''}
      >
        <OrderedListIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editorState.isBlockquote ? 'is-active' : ''}
      >
        <TextQuoteIcon />
      </button>
      <button onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <MinusIcon tooltip='Horizontal Rule' />
      </button>
      {/* <button onClick={() => editor.chain().focus().setHardBreak().run()}>Hard break</button>
      <button onClick={() => editor.chain().focus().undo().run()} disabled={!editorState.canUndo}>
        Undo
      </button>
      <button onClick={() => editor.chain().focus().redo().run()} disabled={!editorState.canRedo}>
        Redo
      </button> */}
      {onSave && <button onClick={() => onSave(JSON.stringify(editor.getJSON()))}>
        <SaveIcon />
      </button>}
      </div>
    </div>
  )
}

export default TiptapEditor

interface TiptapEditorProps {
  content?: string
  onChange?: (content: string) => void
  onBlur?: (content: string) => void
  placeholder?: string
  minHeight?: string
  tabIndex?: number
}

function TiptapEditor(props: TiptapEditorProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  
  const isJson = (str: unknown): str is string => {
    if (typeof str !== 'string') return false
    try {
      JSON.parse(str)
      return true
    } catch {
      return false
    }
  }

  const initialContent: string | object = props.content
    ? isJson(props.content)
      ? JSON.parse(props.content)
      : props.content
    : ''
    
  const editor = useEditor({
    extensions: [
      ...extensions,
      Placeholder.configure({
        placeholder: props.placeholder || 'Start typing here...',
        emptyEditorClass: 'is-editor-empty',
      }),
      PasteCleanupExtension,
    ],
    content: initialContent,
    onBlur: () => {
      setShowMenu(false)
      const value = editor?.isEmpty ? '' : JSON.stringify(editor?.getJSON()) || ''
      if (props.onBlur) {
        props.onBlur(value)
      }
    },
    onUpdate: ({ editor }) => {
      if (props.onChange) {
        props.onChange(JSON.stringify(editor.getJSON()))
      }
    },
    onFocus: () => {
      setShowMenu(true)
    },
  })

  return (

    <div className='flex flex-col relative'>
      {showMenu && (
      // prevent the editor from blurring when clicking toolbar buttons
      <div onMouseDown={(e: React.MouseEvent) => e.preventDefault()}>
        <MenuBar editor={editor} onClose={() => setShowMenu(false)} />
      </div>
      )}
      <EditorContent
      className='w-full px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none rounded-none rounded-b-md bg-table-body mb-2 mt-0 text-table-text'
      style={{ minHeight: props.minHeight || '100px' }}
      editor={editor}
      onClick={() => editor?.chain().focus().run()}
      tabIndex={props.tabIndex ?? 0}
      />
    </div>
  )
}